import { db, auth } from '../firebase';
import { collection, doc, getDocs, query, where, serverTimestamp, writeBatch, getDoc } from 'firebase/firestore';
import { MasterTransaction } from '../types';

import { getSettings } from './settingsService';

const removeUndefined = (obj: any) => {
  const clean: any = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined) {
      clean[key] = obj[key];
    }
  });
  return clean;
};

export const submitCashTip = async (tenantId: string, tip: CashTip) => {
  console.log('DEBUG [submitCashTip] Received tip:', { tenantId, tip });
  console.log('DEBUG [submitCashTip] Raw tip amount:', tip.amount, 'staffId:', tip.staffId);
  const settings = await getSettings(tenantId);
  const payoutMethod = settings?.payoutMethod || 'Paycheck';
  const effectiveLocationId = tip.locationId && tip.locationId !== 'all' ? tip.locationId : 'loc-downtown';
  const effectiveLocationName = tip.locationName || (effectiveLocationId === 'loc-downtown' ? 'Downtown Bistro' : 'Store Location');
  const tipWithMethod = { ...tip, payoutMethod, locationId: effectiveLocationId, locationName: effectiveLocationName };
  
  const batch = writeBatch(db);
  const tipRef = doc(collection(db, 'tenants', tenantId, 'cash_tips'));
  const masterRef = doc(collection(db, 'tenants', tenantId, 'master_transactions'));
  
  batch.set(tipRef, removeUndefined({ ...tipWithMethod, masterTxId: masterRef.id }));

  // Unified General Ledger Entry
  const tx: MasterTransaction = {
    id: masterRef.id,
    tenantId,
    timestamp: serverTimestamp(),
    source: 'Cash',
    type: 'Submitted',
    reportingStatus: 'cash_submitted',
    amount: Math.round(tip.amount * 100) / 100,
    submittedAmount: Math.round(tip.amount * 100) / 100,
    payoutMethod,
    tipDate: tip.tipDate,
    shiftName: tip.shiftName || null,
    staffId: tip.staffId || 'unknown',
    staffName: tip.staffName,
    email: tip.email,
    submittedBy: tip.submittedBy,
    submissionStatus: 'Pending',
    allocationStatus: 'Pending',
    payrollIncluded: false,
    analyticsIncluded: true,
    auditVerified: false,
    auditContext: 'Initial Submission',
    relatedTransactionId: tipRef.id,
    source_id: tipRef.id,
    parentTxId: tip.parentTxId || null,
    posId: tip.posId || null,
    locationId: effectiveLocationId,
    locationName: effectiveLocationName
  };
  batch.set(masterRef, removeUndefined(tx));

  try {
    await batch.commit();
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, 'tenants/' + tenantId + '/cash_tips/master_transactions');
  }
};

export interface CashTip {
  id?: string;
  tenantId: string;
  staffId?: string;
  staffName: string;
  email: string;
  tipDate: string;
  shiftName?: string;
  shiftStartTime?: string;
  shiftEndTime?: string;
  amount: number;
  status: 'Pending' | 'Calculated' | 'Approved' | 'Allocated' | 'Rejected';
  paymentStatus: 'Paid' | 'Unpaid';
  payoutMethod?: string;
  submittedBy: string;
  source: 'Cash';
  approverId?: string;
  approvalTimestamp?: any;
  masterTxId?: string;
  parentTxId?: string;
  notes?: string;
  posId?: string;
  locationId?: string;
  locationName?: string;
}

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export const getCashTips = async (tenantId: string): Promise<CashTip[]> => {
  const tipsRef = collection(db, 'tenants', tenantId, 'cash_tips');
  try {
    const querySnapshot = await getDocs(tipsRef);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CashTip));
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'tenants/' + tenantId + '/cash_tips');
    return [];
  }
};

export const getPendingCashTips = async (tenantId: string): Promise<CashTip[]> => {
  const tipsRef = collection(db, 'tenants', tenantId, 'cash_tips');
  const q = query(tipsRef, where('status', '==', 'Pending'));
  try {
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CashTip));
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'tenants/' + tenantId + '/cash_tips');
    return [];
  }
};

export const getCashTipsByEmail = async (tenantId: string, email: string): Promise<CashTip[]> => {
  const tipsRef = collection(db, 'tenants', tenantId, 'cash_tips');
  const q = query(tipsRef, where('email', '==', email));
  try {
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CashTip));
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'tenants/' + tenantId + '/cash_tips');
    return [];
  }
};

export const updateCashTipStatus = async (
  tenantId: string, 
  tipId: string, 
  masterTxId: string,
  updates: {
    status: CashTip['status'],
    paymentStatus?: 'Paid' | 'Unpaid',
    approverId?: string,
    allocatedAmount?: number
  }
) => {
  try {
    const tipRef = doc(db, 'tenants', tenantId, 'cash_tips', tipId);

    // Fallback: Verify master transaction existence or find it
    let finalMasterTxId = masterTxId;
    let masterRef = doc(db, 'tenants', tenantId, 'master_transactions', finalMasterTxId);
    let masterDoc = await getDoc(masterRef);

    if (!masterDoc.exists() || finalMasterTxId === 'unknown') {
      const q = query(
        collection(db, 'tenants', tenantId, 'master_transactions'), 
        where('relatedTransactionId', '==', tipId)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        finalMasterTxId = snapshot.docs[0].id;
        masterRef = doc(db, 'tenants', tenantId, 'master_transactions', finalMasterTxId);
      } else {
        console.warn(`MasterTransaction not found for tip ${tipId}, skipping master update for this tip.`);
        // Proceed with only updating the tip record if master is missing
        finalMasterTxId = ''; 
      }
    }

    const batch = writeBatch(db);
    batch.update(tipRef, { 
      ...updates,
      approvalTimestamp: serverTimestamp()
    });
    if (finalMasterTxId) {
      batch.update(masterRef, {
        allocatedAmount: updates.allocatedAmount,
        submissionStatus: updates.status === 'Approved' ? 'Finalized' : (updates.status === 'Rejected' ? 'Rejected' : 'Pending'),
        reportingStatus: updates.status === 'Approved' ? 'cash_allocated' : 'cash_submitted',
        auditVerified: updates.status === 'Approved',
        auditActor: updates.approverId || 'system',
        auditTimestamp: serverTimestamp()
      });
    }

    await batch.commit();
  } catch (error) {
    handleFirestoreError(error, OperationType.UPDATE, `tenants/${tenantId}/tip_approval_flow/${tipId}`);
  }
};

export interface MonthlyTipCertification {
  id?: string;
  email: string;
  staffName: string;
  year: number;
  month: number; // 0-11
  totalTips: number;
  certifiedAt: any;
  signatureType: 'type' | 'draw';
  typedSignature?: string;
  drawnSignatureData?: string;
  perjuryCertified: boolean;
}

export const certifyMonthlyTips = async (tenantId: string, cert: Omit<MonthlyTipCertification, 'certifiedAt'>) => {
  const certRef = doc(collection(db, 'tenants', tenantId, 'compliance_certifications'));
  try {
    const certData = {
      ...cert,
      certifiedAt: serverTimestamp()
    };
    const batch = writeBatch(db);
    batch.set(certRef, certData);

    // Also update any cash tips of this employee for this month to mark them as certified (optional audit trail)
    const tipsRef = collection(db, 'tenants', tenantId, 'cash_tips');
    const q = query(tipsRef, where('email', '==', cert.email));
    const snapshot = await getDocs(q);
    snapshot.docs.forEach(d => {
      const tipData = d.data();
      if (tipData.tipDate) {
        const parts = tipData.tipDate.split('-');
        if (parts.length === 3) {
          const tYear = parseInt(parts[0], 10);
          const tMonth = parseInt(parts[1], 10) - 1;
          if (tYear === cert.year && tMonth === cert.month) {
            batch.update(d.ref, { certifiedByEmployee: true, certificationId: certRef.id });
          }
        }
      }
    });

    await batch.commit();
    return certRef.id;
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, `tenants/${tenantId}/compliance_certifications`);
  }
};

export const getMonthlyCertifications = async (tenantId: string, year: number, month: number): Promise<MonthlyTipCertification[]> => {
  const certsRef = collection(db, 'tenants', tenantId, 'compliance_certifications');
  const q = query(certsRef, where('year', '==', year), where('month', '==', month));
  try {
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MonthlyTipCertification));
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, `tenants/${tenantId}/compliance_certifications`);
    return [];
  }
};

export const getEmployeeCertification = async (tenantId: string, email: string, year: number, month: number): Promise<MonthlyTipCertification | null> => {
  const certsRef = collection(db, 'tenants', tenantId, 'compliance_certifications');
  const q = query(
    certsRef, 
    where('email', '==', email), 
    where('year', '==', year), 
    where('month', '==', month)
  );
  try {
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as MonthlyTipCertification;
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, `tenants/${tenantId}/compliance_certifications`);
    return null;
  }
};

